import { Inject, Injectable } from '@angular/core';
import { State, StateContext, Action, Selector } from '@ngxs/store';
import {
  BookingDTO,
  FlightBookingDTO,
  HourBookingDTO,
  PageBookingDTO,
  PageGarageBookingDTO,
  BookingApi,
  FlightApi,
  SettingApi,
  FindCurrentFlightsFlightTypeEnum,
} from '@lkp/parking-sdk';
import {
  BOOKING_API,
  FLIGHT_API,
  SETTING_API,
} from '../../providers/api.module';
import * as Actions from './booking.actions';
import { forkJoin, tap } from 'rxjs';
// import { BookingService } from '../../services/booking.service';

export interface BookingsStateModel {
  currentBooking?: BookingDTO;
  currentBookings: BookingDTO[];
  allBookings?: PageBookingDTO;
  currentArrivalBookings: FlightBookingDTO[];
  currentDepartureBookings: HourBookingDTO[];
  currentParkedCarBookings?: PageGarageBookingDTO;
  currentEditBooking?: BookingDTO;
  searchResult?: BookingDTO[];
  searchResultUpcoming?: BookingDTO[];
  searchResultHistory?: BookingDTO[];
  upcomingTimeRange?: { fromHour: Date; toHour: Date };
}

@State<BookingsStateModel>({
  name: 'bookings',
  defaults: {
    currentBooking: undefined,
    currentBookings: [],
    allBookings: undefined,
    currentArrivalBookings: [],
    currentDepartureBookings: [],
    currentParkedCarBookings: undefined,
    currentEditBooking: undefined,
    searchResult: undefined,
    searchResultUpcoming: undefined,
    searchResultHistory: undefined,
    upcomingTimeRange: undefined,
  },
})
@Injectable()
export class BookingsState {
  constructor(
    @Inject(BOOKING_API) readonly bookingApi: BookingApi,
    @Inject(FLIGHT_API) readonly flightApi: FlightApi,
    @Inject(SETTING_API) readonly settingApi: SettingApi
  ) {}

  @Selector()
  static currentBookings(state: BookingsStateModel) {
    return state.currentBookings;
  }

  @Selector()
  static currentBooking(state: BookingsStateModel) {
    return state.currentBooking;
  }

  @Selector()
  static currentEditBooking(state: BookingsStateModel) {
    return state.currentEditBooking;
  }

  @Selector()
  static currentArrivalBookings(state: BookingsStateModel) {
    return state.currentArrivalBookings;
  }

  @Selector()
  static currentDepartureBookings(state: BookingsStateModel) {
    return state.currentDepartureBookings;
  }

  @Selector()
  static currentParkedCarBookings(state: BookingsStateModel) {
    return state.currentParkedCarBookings;
  }

  @Selector()
  static allBookings(state: BookingsStateModel) {
    return state.allBookings;
  }

  @Selector()
  static searchResult(state: BookingsStateModel) {
    return state.searchResult;
  }

  @Selector()
  static searchResultUpcoming(state: BookingsStateModel) {
    return state.searchResultUpcoming;
  }

  @Selector()
  static searchResultHistory(state: BookingsStateModel) {
    return state.searchResultHistory;
  }

  @Selector()
  static upcomingTimeRange(state: BookingsStateModel) {
    return state.upcomingTimeRange;
  }

  @Action(Actions.GetBooking)
  getBooking(
    context: StateContext<BookingsStateModel>,
    action: Actions.GetBooking
  ) {
    return this.bookingApi.getBookingAdmin({ bookingId: action.id }).pipe(
      tap({
        next: (data) => {
          context.patchState({ currentBooking: data });
        },
        error: (error) => {
          console.error('Error fetching booking: ', error);
        },
      })
    );
  }

  @Action(Actions.GetEditBooking)
  getEditBooking(
    context: StateContext<BookingsStateModel>,
    action: Actions.GetEditBooking
  ) {
    return (
      action.id &&
      this.bookingApi.getBookingAdmin({ bookingId: action.id }).pipe(
        tap({
          next: (data) => {
            context.patchState({ currentEditBooking: data });
          },
          error: (error) => {
            console.error('Error fetching edit booking: ', error);
          },
        })
      )
    );
  }

  @Action(Actions.DoneEditBooking)
  doneEditBooking(context: StateContext<BookingsStateModel>) {
    return context.patchState({ currentEditBooking: undefined });
  }

  @Action(Actions.GetCurrentBookings)
  getCurrentBookings(context: StateContext<BookingsStateModel>) {
    return forkJoin([
      this.flightApi.findCurrentFlights({
        flightType: FindCurrentFlightsFlightTypeEnum.Arrival,
        fromDate: undefined,
        toDate: undefined,
      }),
      this.flightApi.findCurrentDepartureBookings({
        fromDate: undefined,
        toDate: undefined,
      }),
    ]).pipe(
      tap(([currentArrivals, currentDepartures]) => {
        // Update state
        context.patchState({ currentDepartureBookings: currentDepartures });
        context.patchState({ currentArrivalBookings: currentArrivals });
        context.patchState({
          currentBookings: [
            ...(currentArrivals || []),
            ...(currentDepartures || []),
          ],
        });
      })
    );
  }

  @Action(Actions.GetParkedCarBookings)
  getParkedCarBookings(
    context: StateContext<BookingsStateModel>,
    action: Actions.GetParkedCarBookings
  ) {
    return this.bookingApi
      .getGarageBookingsAdmin({
        page: action.pageIndex,
        size: action.pageSize,
        sort: action.sortOrder || undefined,
        resourceName: action.resourceName,
        searchTerm: action.searchTerm || undefined,
        arrivalFromDate: undefined,
        arrivalToDate: undefined,
        mainFeatureType: action.mainFeatureType || undefined,
      })
      .pipe(
        tap((v) => {
          context.patchState({ currentParkedCarBookings: v });
        })
      );
  }

  @Action(Actions.GetAllBookings)
  getAllBookings(
    context: StateContext<BookingsStateModel>,
    action: Actions.GetAllBookings
  ) {
    const column = action.sortColumn ? action.sortColumn : 'arrivalDate';

    const departureFromDate: Date | undefined =
      action.dateFilterType === 'departure' && action.startDate
        ? action.startDate
        : undefined;

    const departureToDate: Date | undefined =
      action.dateFilterType === 'departure' && action.endDate
        ? action.endDate
        : undefined;

    const arrivalFromDate: Date | undefined =
      action.dateFilterType === 'arrival' && action.startDate
        ? action.startDate
        : undefined;

    const arrivalToDate: Date | undefined =
      action.dateFilterType === 'arrival' && action.endDate
        ? action.endDate
        : undefined;

    return this.bookingApi
      .findBookingsAdmin({
        page: action.pageIndex,
        size: action.pageSize,
        sort: `${column},${action.sortOrder}`,
        searchTerm: action.searchTerm || undefined,
        departureFromDate,
        departureToDate,
        arrivalFromDate,
        arrivalToDate,
        resourceName: action.resourceName,
        mainFeatureType: action.mainFeatureType,
      })
      .pipe(
        tap((v) => {
          context.patchState({ allBookings: v });
        })
      );
  }

  @Action(Actions.FindBookings)
  findBookings(
    context: StateContext<BookingsStateModel>,
    action: Actions.FindBookings
  ) {
    const today = new Date(new Date().setHours(0, 0, 0));
    const yesterday = new Date(new Date().setHours(0, -1, 0));

    return forkJoin([
      this.bookingApi.getBookingsAdmin({
        page: undefined,
        size: undefined,
        sort: 'departureDate,asc',
        searchTerm: action.searchTerm || undefined,
        arrivalFlightNumber: undefined,
        registrationNumber: undefined,
        departureFromDate: undefined,
        departureToDate: undefined,
        arrivalFromDate: today,
        arrivalToDate: undefined,
        resourceName: undefined,
      }),
      this.bookingApi.getBookingsAdmin({
        page: undefined,
        size: undefined,
        sort: 'arrivalDate,desc',
        searchTerm: action.searchTerm || undefined,
        arrivalFlightNumber: undefined,
        registrationNumber: undefined,
        departureFromDate: undefined,
        departureToDate: undefined,
        arrivalFromDate: undefined,
        arrivalToDate: yesterday,
        resourceName: undefined,
      }),
    ]).pipe(
      tap(([upcomingBookings, historyBookings]) => {
        context.patchState({ searchResultUpcoming: upcomingBookings.content });
        context.patchState({ searchResultHistory: historyBookings.content });
      })
    );
  }

  @Action(Actions.FindBookingsByDirection)
  findBookingsByDirection(
    context: StateContext<BookingsStateModel>,
    action: Actions.FindBookingsByDirection
  ) {
    const fromHour = context
      .getState()
      .upcomingTimeRange?.fromHour;
    const toHour = context.getState().upcomingTimeRange?.toHour;

    if (action.direction == 'arrival') {
      return this.bookingApi
        .getBookingsAdmin({
          page: undefined,
          size: undefined,
          sort: 'arrivalDate,asc',
          searchTerm: action.searchTerm || undefined,
          arrivalFlightNumber: undefined,
          registrationNumber: undefined,
          departureFromDate: undefined,
          departureToDate: undefined,
          arrivalFromDate: fromHour,
          arrivalToDate: toHour,
          resourceName: undefined,
        })
        .pipe(
          tap((value) => {
            context.patchState({ searchResult: value.content });
          })
        );
    } else {
      return this.bookingApi
        .getBookingsAdmin({
          page: undefined,
          size: undefined,
          sort: 'departureDate,asc',
          searchTerm: action.searchTerm || undefined,
          arrivalFlightNumber: undefined,
          registrationNumber: undefined,
          departureFromDate: fromHour,
          departureToDate: toHour,
          arrivalFromDate: undefined,
          arrivalToDate: undefined,
          resourceName: undefined,
        })
        .pipe(
          tap((value) => {
            context.patchState({ searchResult: value.content });
          })
        );
    }
  }

  @Action(Actions.GetUpcomingTimeRange)
  getUpcomingTimeRange(
    context: StateContext<BookingsStateModel>,
    action: Actions.GetUpcomingTimeRange
  ) {
    const now = new Date();

    return forkJoin([
      this.settingApi.getSetting({ name: 'CURRENT_BOOKING_FROM_MINUTES' }),
      this.settingApi.getSetting({ name: 'CURRENT_BOOKING_TO_MINUTES' }),
    ]).pipe(
      tap(([currentFromMinutesOffset, currentToMinutesOffset]) => {
        let fromHoursOffset = Math.trunc(
          Number(currentFromMinutesOffset.value) / 60
        );
        const fromMinutesOffset =
          now.getMinutes() -
          Math.abs(Number(currentFromMinutesOffset.value) % 60);
        let toHoursOffset = Math.trunc(
          Number(currentToMinutesOffset.value) / 60
        );
        let toMinutesOffset =
          now.getMinutes() + (Number(currentToMinutesOffset.value) % 60);

        if (fromMinutesOffset < 0) {
          fromHoursOffset--;
        }

        if (toMinutesOffset > 60) {
          toHoursOffset++;
          toMinutesOffset -= 60;
        }

        const minRoundDown =
          (Math.round((Math.abs(fromMinutesOffset) - 7.5) / 15) * 15) % 60; // nearest 15 min down, 00, 15, 30, 45
        const minRoundUp = (Math.round((toMinutesOffset + 7.4) / 15) * 15) % 60; // nearest 15 min up, 00, 15, 30, 45
        if (toMinutesOffset > 45) toHoursOffset++;

        const fromHour = new Date(
          new Date(now).setHours(
            now.getHours() + fromHoursOffset,
            minRoundDown,
            0
          )
        );
        const toHour = new Date(
          new Date(now).setHours(now.getHours() + toHoursOffset, minRoundUp, 0)
        );

        // Update state
        context.patchState({ upcomingTimeRange: { fromHour, toHour } });
      })
    );
  }

  @Action(Actions.UpdateQtyPersonsCheckedOff)
  updateQtyPersonsCheckedOff(
    context: StateContext<BookingsStateModel>,
    action: Actions.UpdateQtyPersonsCheckedOff
  ) {
    const { direction, bookingId, qtyPersonsCheckedOff } = action;
    if (direction === 'arrival') {
      return this.bookingApi
        .updateQtyPersonsPickedUp({
          bookingId,
          qtyPersonPickedUpRequest: {
            bookingId,
            qtyPersonsPickedUp: qtyPersonsCheckedOff,
          },
        })
        .pipe(
          tap((v) => {
            context.dispatch(new Actions.GetCurrentBookings());
            context.patchState({ currentBooking: v });
          })
        );
    } else if (direction === 'departure') {
      return this.bookingApi
        .updateQtyPersonsDroppedOff({
          bookingId,
          qtyPersonPickedUpRequest: {
            bookingId,
            qtyPersonsPickedUp: qtyPersonsCheckedOff,
          },
        })
        .pipe(
          tap((v) => {
            context.dispatch(new Actions.GetCurrentBookings());
            context.patchState({ currentBooking: v });
          })
        );
    }
    return;
  }

  @Action(Actions.UpdateParkingStatus)
  updateParkingStatus(
    context: StateContext<BookingsStateModel>,
    action: Actions.UpdateParkingStatus
  ) {
    const { bookingId, eventType, checked } = action;

    return this.bookingApi
      .updateBookingEvent({
        bookingId,
        updateBookingEvent: {
          bookingId,
          bookingEventType: eventType,
          checked,
        },
      })
      .pipe(
        tap((v) => {
          context.patchState({ currentBooking: v });
        })
      );
  }

  @Action(Actions.CancelBooking)
  cancelBooking(
    context: StateContext<BookingsStateModel>,
    action: Actions.CancelBooking
  ) {
    const { bookingId } = action;
    return this.bookingApi.cancelBooking({ bookingId }).pipe(
      tap((v) => {
        context.dispatch(new Actions.GetCurrentBookings());
      })
    );
  }

  @Action(Actions.ResetBookingState)
  resetState(context: StateContext<BookingsStateModel>) {
    context.setState({
      currentBooking: undefined,
      currentBookings: [],
      allBookings: undefined,
      currentArrivalBookings: [],
      currentDepartureBookings: [],
      currentParkedCarBookings: undefined,
      currentEditBooking: undefined,
      searchResult: undefined,
      searchResultUpcoming: undefined,
      searchResultHistory: undefined,
      upcomingTimeRange: undefined,
    });
  }
}
